import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { requestPasswordReset } from "@/lib/api";
import { AuthLayout, AuthNotice, BackToSignIn, PanelBlock, APP_NAME } from "@/components/auth/AuthLayout";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  useEffect(() => {
    document.title = `Forgot password | ${APP_NAME}`;
    return () => { document.title = APP_NAME; };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus("loading");
    setMessage("");
    try {
      await requestPasswordReset(email.trim());
      setStatus("success");
      setMessage("If an account exists for this email, you will receive reset instructions.");
    } catch (err: any) {
      setStatus("error");
      setMessage(err?.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <AuthLayout
      title="Forgot password?"
      subtitle="Enter your email and we'll send you a link to reset your password."
      back={<BackToSignIn />}
      panel={
        <PanelBlock title="Locked out? It happens.">
          <p>We'll email you a link to set a new password. Nothing in your clinic's records changes.</p>
          <p>No email, or it doesn't arrive? Your clinic admin can reset your password from Settings, and you choose a new one when you next sign in.</p>
        </PanelBlock>
      }
    >
      {status === "success" ? (
        <div className="space-y-4">
          <AuthNotice tone="success">{message}</AuthNotice>
          <p className="text-sm text-muted-foreground">
            Didn't get it? Check your spam folder,{" "}
            <button type="button" onClick={() => setStatus("idle")} className="touch-target font-medium text-primary hover:underline">try another email</button>,
            or ask your clinic admin to reset your password from Settings.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@clinic.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={status === "loading"}
              required
              autoFocus
              autoComplete="email"
              className="h-11"
            />
          </div>

          {status === "error" && message && <AuthNotice tone="error">{message}</AuthNotice>}

          <Button type="submit" disabled={status === "loading"} className="w-full h-11">
            {status === "loading" ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : "Send reset link"}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
